<template>
  <div class="import-contacts flex flex-col">
    <div
      class="flex flex-col items-start pt-6 px-6 pb-4 border-b border-gray-100"
      style="box-shadow: 0px 1px 4px 0px rgba(202, 202, 202, 0.16)"
    >
      <div
        class="text-gray-800 text-lg leading-5 dark:text-slate-100 font-semibold w-full"
      >
        <span class="flex items-center gap-2">
          <v2-icon icon="download" class="text-gray-500" />
          {{ $t('IMPORT_CONTACTS.TITLE') }}
        </span>
      </div>
      <div class="text-sm leading-5 text-gray-600 dark:text-slate-300 mt-2">
        {{ $t('IMPORT_CONTACTS.DESC') }}
        <a
          :href="csvUrl"
          download="import-contacts-sample"
          class="underline text-green-500 hover:text-green-600 transition-colors"
        >
          {{ $t('IMPORT_CONTACTS.DOWNLOAD_LABEL') }}
        </a>
      </div>
    </div>

    <div class="px-6 py-4 flex flex-col gap-4">
      <div v-if="statusImport" class="flex flex-col gap-1.5">
        <div class="flex items-center gap-2">
          <span class="text-sm leading-5 text-gray-700 dark:text-slate-500">
            {{ $t('INBOX_MGMT.IMPORT_CONVERSATIONS.LAST_RESULT_TITLE') }}
          </span>
          <woot-button
            color-scheme="secondary"
            variant="link"
            icon="ion-android-refresh"
            @click="fetchImportStatus"
          />
        </div>
        <div
          class="bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 w-full overflow-auto max-h-32"
        >
          <code
            class="bg-gray-100 text-gray-500 dark:text-slate-400 text-base leading-6 whitespace-pre-line break-all p-0"
            >{{ statusImportText }}
          </code>
        </div>
      </div>

      <input
        id="file"
        ref="file"
        type="file"
        accept=".csv,.xlsx"
        class="hidden"
        @change="handleFileUpload"
      />

      <div
        v-if="uploadState === 'idle'"
        class="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-[26px] border-2 border-dashed bg-white py-11 transition-colors dark:bg-slate-900"
        :class="
          isDragging ? 'border-brand' : 'border-[#dfdcdc] hover:border-brand/40'
        "
        @click="$refs.file.click()"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <div class="flex flex-col items-center gap-2 px-4 text-center">
          <p class="dropzone-title">
            {{ $t('IMPORT_CONTACTS.FORM.DROPZONE_LABEL') }}
          </p>
          <p class="dropzone-subtitle">
            {{ $t('IMPORT_CONTACTS.FORM.DROPZONE_FILE_LIMIT') }}
          </p>
        </div>
      </div>

      <div
        v-else-if="uploadState === 'success'"
        class="relative flex items-start gap-3 rounded-[12px] border border-[#baf3db] bg-[#f0fdf4] p-6"
      >
        <div class="flex shrink-0 items-center rounded-[8px] bg-[#dcfff1] p-2">
          <v2-icon icon="note" class="text-brand-500" />
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <p class="file-card-name truncate">{{ file.name }}</p>
          <p class="file-card-size">{{ formattedFileSize }}</p>
        </div>
        <button
          type="button"
          class="absolute right-[14px] top-[8px] cursor-pointer text-[#6b7280] transition-colors hover:text-primary rtl:right-auto rtl:left-[14px]"
          @click="clearFile"
        >
          <v2-icon icon="close_small" size="24" />
        </button>
      </div>

      <div
        v-else-if="uploadState === 'error'"
        class="relative flex flex-col gap-3 rounded-[12px] border border-red-200 bg-red-50 p-6"
      >
        <div class="flex items-start gap-3">
          <div class="flex shrink-0 items-center rounded-[8px] bg-red-100 p-2">
            <v2-icon icon="note" class="text-red-500" />
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <p class="file-card-name truncate">{{ file.name }}</p>
            <p class="file-card-size">{{ formattedFileSize }}</p>
          </div>
        </div>
        <p class="file-card-error">{{ fileError }}</p>
        <button
          type="button"
          class="absolute right-[13px] top-[8px] cursor-pointer text-[#6b7280] transition-colors hover:text-primary rtl:right-auto rtl:left-[13px]"
          @click="clearFile"
        >
          <v2-icon icon="close_small" size="24" />
        </button>
      </div>
    </div>

    <div
      class="flex items-center justify-end gap-3 pb-6 pt-4 px-6 border-t border-gray-100"
      style="box-shadow: 0px -1px 4px 0px rgba(202, 202, 202, 0.16)"
    >
      <button-v2
        variant="naked"
        size="lg"
        class="cancel-filter-btn"
        @click.prevent="onClose"
      >
        {{ $t('IMPORT_CONTACTS.FORM.CANCEL') }}
      </button-v2>
      <button-v2
        variant="filled"
        size="lg"
        :disabled="uiFlags.isCreating || uploadState !== 'success'"
        :loading="uiFlags.isCreating"
        class="apply-filter-btn"
        @click="uploadFile"
      >
        {{ $t('IMPORT_CONTACTS.FORM.SUBMIT') }}
      </button-v2>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ButtonV2 from 'v2/components/Button/ButtonV2.vue';
import V2Icon from 'v2/components/Icon.vue';

export default {
  components: { ButtonV2, V2Icon },
  mixins: [alertMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    this.fetchImportStatus();
    return {
      file: '',
      fileError: null,
      uploadState: 'idle',
      isDragging: false,
      statusImportText: undefined,
      statusImport: undefined,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
    }),
    csvUrl() {
      return '/downloads/import-contacts-sample.xlsx';
    },
    formattedFileSize() {
      if (!this.file) return '';
      const bytes = this.file.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
  },
  mounted() {
    this.$track(CONTACTS_EVENTS.IMPORT_MODAL_OPEN);
  },
  methods: {
    async uploadFile() {
      try {
        if (this.uploadState !== 'success' || !this.file) return;
        await this.$store.dispatch('contacts/import', this.file);
        this.onClose();
        this.showAlert(this.$t('IMPORT_CONTACTS.SUCCESS_MESSAGE'));
        this.$track(CONTACTS_EVENTS.IMPORT_SUCCESS);
      } catch (error) {
        this.showAlert(
          error.message || this.$t('IMPORT_CONTACTS.ERROR_MESSAGE')
        );
        this.$track(CONTACTS_EVENTS.IMPORT_FAILURE);
      }
    },
    processFile(pickedFile) {
      const name = (pickedFile.name || '').toLowerCase();
      const isValidType = name.endsWith('.csv') || name.endsWith('.xlsx');
      if (!isValidType) {
        this.file = pickedFile;
        this.fileError = this.$t(
          'IMPORT_CONTACTS.FORM.FILE_ERROR.INVALID_TYPE'
        );
        this.uploadState = 'error';
        return;
      }
      if (pickedFile.size > 10 * 1024 * 1024) {
        this.file = pickedFile;
        this.fileError = this.$t('IMPORT_CONTACTS.FORM.FILE_ERROR.TOO_LARGE');
        this.uploadState = 'error';
        return;
      }
      this.file = pickedFile;
      this.fileError = null;
      this.uploadState = 'success';
    },
    clearFile() {
      this.file = '';
      this.fileError = null;
      this.uploadState = 'idle';
      this.isDragging = false;
      if (this.$refs.file) this.$refs.file.value = '';
    },
    handleDrop(event) {
      this.isDragging = false;
      const dropped = event.dataTransfer.files[0];
      if (dropped) this.processFile(dropped);
    },
    handleFileUpload() {
      const picked = this.$refs.file.files[0];
      if (picked) this.processFile(picked);
    },
    async fetchImportStatus() {
      try {
        const { status, message } = await this.$store.dispatch(
          'contacts/statusOfLastImport'
        );
        this.statusImport = status;
        this.statusImportText = message;
      } catch (error) {
        this.showAlert(
          error.message ||
            this.$t('INBOX_MGMT.IMPORT_CONVERSATIONS.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped>
.dropzone-title {
  font-size: 16px !important;
  line-height: 1.2 !important;
  color: #323431 !important;
  margin: 0 !important;
}

.dropzone-subtitle {
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: #717171 !important;
  margin: 0 !important;
}

.file-card-name {
  font-size: 16px !important;
  line-height: 24px !important;
  font-weight: 400 !important;
  color: #323431 !important;
  margin: 0 !important;
}

.file-card-size {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #5c5c5c !important;
  margin: 0 !important;
}

.file-card-error {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #ef4444 !important;
  margin: 0 !important;
}
</style>
